import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import api from 'state/axios';
import toast from 'react-hot-toast';
import {
    useTheme, 
    Box, 
    Typography, 
    Divider, 
    IconButton, 
    Menu, 
    MenuItem,
    Card, 
    CardContent, 
    CardActions, 
    Collapse, 
    Avatar, 
    AvatarGroup,
    Chip, 
    Badge, 
    Tooltip, 
    Table, 
    TableHead, 
    TableBody, 
    TableRow, 
    TableCell,
} from '@mui/material';
import { 
    MoreVert,
    ExpandMore,
    People,
    Print,
    Share,
    FavoriteBorderOutlined, 
    FavoriteOutlined,
} from "@mui/icons-material";
import { hasPermission } from 'state/checkPermission';
import UserImage from "components/UserImage";
import DeleteConfirm from 'components/DeleteConfirm';
import { handlePrint } from './ShiftReportPrint';


const ReportCard = ({ report, onDeleted }) => {
    const navigate = useNavigate();
    const user = useSelector((state) => state.user);
    const theme = useTheme();

    const canEdit = hasPermission(user?.permissions, "ShiftReports", "update", false);
    const canDelete = hasPermission(user?.permissions, "ShiftReports", "delete", false);

    const shiftColor = report.shift?.color || theme.palette.primary.main;
    const shiftBg = shiftColor + '22';

    const [todosExpanded, setTodosExpanded] = useState(false);
    const [readExpanded, setReadExpanded] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deleteType, setDeleteType] = useState(null);
    const [idToDelete, setIdToDelete] = useState(null);
    const [isLiked, setIsLiked] = useState(Boolean(report.likes?.[user._id]));
    const [likeCount, setLikeCount] = useState(report.likes ? Object.keys(report.likes).length : 0);
    const [readBy, setReadBy] = useState(report.readBy ?? []);
    const hasMarkedRead = useRef(false);


    useEffect(() => {
        setIsLiked(Boolean(report.likes?.[user._id]));
        setLikeCount(report.likes ? Object.keys(report.likes).length : 0);
        setReadBy(report.readBy ?? []);
        hasMarkedRead.current = false;
    }, [report, user._id]);

    const handleMoreClick = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleEdit = () => navigate(`/shiftreports/${report._id}`);

    const handleDeleteClick = (id) => {
        setIdToDelete(id);
        setDeleteType('single');
        setConfirmDialogOpen(true);
    };

    const executeDelete = useCallback(async () => {
        try {
            if (!canDelete || !idToDelete) return;
            await api.delete(`/shiftreports/delete/${idToDelete}`);
            toast.success("Report deleted successfully.");
            onDeleted?.(idToDelete);
        } catch (err) {
            toast.error(err?.message || "Failed to delete shift report!");
        }
    }, [canDelete, idToDelete, onDeleted]);

    const handleLike = async () => {
        try {
            const res = await api.patch(
                `/shiftreports/${report._id}/like`,
                { userId: user._id }
            );
            setIsLiked(Boolean(res.data.likes?.[user._id]));
            setLikeCount(res.data.likes ? Object.keys(res.data.likes).length : 0);
        } catch (err) {
            toast.error(err?.message || "Failed to update like!");
        }
    };

    const handleReadExpand = useCallback(async () => {
        setReadExpanded(prev => !prev);
        if (!hasMarkedRead.current) {
            hasMarkedRead.current = true;
            try {
                const res = await api.patch(
                    `/shiftreports/${report._id}/read`,
                    {}
                );
                setReadBy(res.data.readBy ?? []);
            } catch {
                // silent fail
            }
        }
    }, [report._id]);

    const handleShare = () => {
        const recipientEmails = report.recipients?.map(r => r.email) || [];
        const allEmails = [...recipientEmails, user.email].join(",");
        const subject = encodeURIComponent(report.title || "Shift Report");
        const body = encodeURIComponent(
            (report.content || '').replace(/<\/?[^>]+(>|$)/g, "").trim()
        );
        window.location.href = `mailto:${allEmails}?subject=${subject}&body=${body}`;
    };

    const creatorName = report.creator
        ? `${report.creator.lastName?.toUpperCase()} ${report.creator.firstName?.toUpperCase()}`
        : "Unknown";

    const fs = report.reportSummary;

    const otpColor = (val) => {
        if (val == null) return 'default';
        if (val >= 85) return 'success';
        if (val >= 70) return 'warning';
        return 'error';
    };

    return (
        <Card
            variant="outlined"
            sx={{ borderLeft: `2px solid ${shiftColor}` }}
        >
            {/* ── HEADER BAND ── */}
            <Box
                display="flex"
                alignItems="center"
                gap={1}
                px={2}
                py={0.75}
                sx={{ backgroundColor: shiftBg }}
            >
                <Chip
                    label={report.shift?.shiftName || report.shift?.shiftCode || 'Shift'}
                    size="small"
                    sx={{
                        bgcolor: shiftColor,
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                    }}
                />
                <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
                    {report.title}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                    {report.reportDate ? format(new Date(report.reportDate), 'dd/MM/yyyy') : ''}
                </Typography>
                <IconButton size="small" onClick={handleMoreClick}>
                    <MoreVert fontSize="small" />
                </IconButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                    {canEdit && (
                        <MenuItem onClick={() => { handleEdit(); handleClose(); }}>Edit</MenuItem>
                    )}
                    {canDelete && (
                        <MenuItem onClick={() => { handleDeleteClick(report._id); handleClose(); }}>
                            Delete
                        </MenuItem>
                    )}
                </Menu>
            </Box>

            {/* ── INFO ROW ── */}
            <Box display="flex" alignItems="center" gap={1} px={2} py={0.5} flexWrap="wrap">
                <Avatar sx={{ width: 28, height: 28 }}>
                    <UserImage image={report.creator?.picturePath} size="28px" />
                </Avatar>
                <Typography variant="body2" fontWeight={500}>{creatorName}</Typography>
                <Divider orientation="vertical" flexItem />
                <Typography variant="caption" color="textSecondary">
                    {report.createdAt
                        ? format(new Date(report.createdAt), 'HH:mm dd/MM/yy')
                        : ''}
                </Typography>
                {report.updatedAt && report.updatedAt !== report.createdAt && (
                    <>
                        <Divider orientation="vertical" flexItem />
                        <Typography variant="caption" color="textSecondary">
                            Updated: {format(new Date(report.updatedAt), 'HH:mm dd/MM/yy')}
                        </Typography>
                    </>
                )}
            </Box>

            {/* ── CONTENT ── */}
            <CardContent sx={{ pb: 0 }}>
                <Typography variant="body1" dangerouslySetInnerHTML={{ __html: report.content }} />
            </CardContent>

            {/* ── FLIGHT SUMMARY ── */}
            {fs && (
                <CardContent sx={{ pt: 1, pb: 0 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="caption" color="textSecondary" fontWeight={600} display="block" mb={0.5}>
                        SUMMARY
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.75}>
                        <Chip size="small" variant="outlined" label={`Flights: ${fs.totalFlights ?? '-'}`} />
                        <Chip size="small" variant="outlined" color={fs.cancelled > 0 ? 'error' : 'default'} label={`Cancelled: ${fs.cancelled ?? 0}`} />
                        <Chip size="small" variant="outlined" color={fs.diverted  > 0 ? 'warning' : 'default'} label={`Diverted: ${fs.diverted ?? 0}`} />
                        <Chip size="small" variant="outlined" label={`Block Hrs: ${fs.totalBlockHours ?? '-'}`} />
                        <Chip size="small" variant="outlined" color={otpColor(fs.depOTP)}  label={`Dep OTP: ${fs.depOTP != null ? fs.depOTP + '%' : '-'}`} />
                        <Chip size="small" variant="outlined" color={otpColor(fs.arrOTP)}  label={`Arr OTP: ${fs.arrOTP != null ? fs.arrOTP + '%' : '-'}`} />
                        <Chip size="small" variant="outlined" label={`Avg Dep Delay: ${fs.avgDelayDep != null ? fs.avgDelayDep + ' min' : '-'}`} />
                        <Chip size="small" variant="outlined" label={`Avg Arr Delay: ${fs.avgDelayArr != null ? fs.avgDelayArr + ' min' : '-'}`} />
                    </Box>
                </CardContent>
            )}

            {/* ── RECIPIENTS ── */}
            {report.recipients?.length > 0 && (
                <Box display="flex" alignItems="center" gap={1} px={2} pt={1}>
                    <Typography variant="caption" color="textSecondary">To:</Typography>
                    <AvatarGroup max={6} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.65rem' } }}>
                        {report.recipients.map((r) => (
                            <Tooltip
                                key={r._id}
                                title={`${r.lastName?.toUpperCase()} ${r.firstName?.toUpperCase()}`}
                                arrow
                            >
                                <Avatar sx={{ width: 24, height: 24 }}>
                                    <UserImage image={r.picturePath} size="24px" />
                                </Avatar>
                            </Tooltip>
                        ))}
                    </AvatarGroup>
                </Box>
            )}

            {/* ── TODOS (Collapse) ── */}
            <Collapse in={todosExpanded} timeout="auto" unmountOnExit>
                <CardContent>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="h6" color={shiftColor} sx={{ mb: 1 }}>
                        Todos Checklist
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '5%' }}><strong>#</strong></TableCell>
                                <TableCell sx={{ width: '45%' }}><strong>Task</strong></TableCell>
                                <TableCell sx={{ width: '20%' }}><strong>Status</strong></TableCell>
                                <TableCell sx={{ width: '30%' }}><strong>Performer</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {report.todos?.length ? (
                                report.todos.map((todo, idx) => {
                                    const performerName = todo.performer?.lastName && todo.performer?.firstName
                                        ? `${todo.performer.lastName.toUpperCase()} ${todo.performer.firstName.toUpperCase()}`
                                        : null;
                                    return (
                                        <TableRow key={idx}>
                                            <TableCell>{idx + 1}</TableCell>
                                            <TableCell>{todo.todo}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={todo.status}
                                                    size="small"
                                                    color={todo.status === 'Completed' ? 'success' : 'warning'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>{performerName ?? '-'}</TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4}>No todos available.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Collapse>

            {/* ── READ BY (Collapse) ── */}
            <Collapse in={readExpanded} timeout="auto" unmountOnExit>
                <CardContent>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="caption" color="textSecondary" fontWeight={600} display="block" mb={0.75}>
                        READ BY ({readBy.length})
                    </Typography>
                    {readBy.length > 0 ? (
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {readBy.map((rb, idx) => {
                                const name = rb.user?.lastName && rb.user?.firstName
                                    ? `${rb.user.lastName.toUpperCase()} ${rb.user.firstName.toUpperCase()}`
                                    : 'Unknown';
                                const timeStr = rb.readAt
                                    ? format(new Date(rb.readAt), 'HH:mm dd/MM/yy')
                                    : '';
                                return (
                                    <Tooltip key={idx} title={`${name} — ${timeStr}`} arrow>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Avatar sx={{ width: 28, height: 28 }}>
                                                <UserImage image={rb.user?.picturePath} size="28px" />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="caption" display="block" lineHeight={1.2}>
                                                    {name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary" display="block" lineHeight={1.2}>
                                                    {timeStr}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    ) : (
                        <Typography variant="caption" color="textSecondary">
                            No one has read this report yet.
                        </Typography>
                    )}
                </CardContent>
            </Collapse>

            {/* ── ACTIONS ── */}
            <CardActions disableSpacing sx={{ px: 2, pt: 0.5 }}>
                <Typography variant="body2" color="textSecondary">{likeCount}</Typography>
                <IconButton size="small" onClick={handleLike}>
                    {isLiked
                        ? <FavoriteOutlined sx={{ color: shiftColor }} fontSize="small" />
                        : <FavoriteBorderOutlined fontSize="small" />
                    }
                </IconButton>
                <IconButton size="small" onClick={() => handlePrint(report)}>
                    <Print fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleShare}>
                    <Share fontSize="small" />
                </IconButton>

                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={`Read by ${readBy.length}`} arrow>
                        <IconButton size="small" onClick={handleReadExpand}>
                            <Badge badgeContent={readBy.length} color="primary" max={99}>
                                <People fontSize="small" />
                            </Badge>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={todosExpanded ? 'Hide todos' : `Todos (${report.todos?.length ?? 0})`} arrow>
                        <IconButton
                            size="small"
                            onClick={() => setTodosExpanded(p => !p)}
                            sx={{
                                transform: todosExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s',
                            }}
                        >
                            <ExpandMore fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </CardActions>

            <DeleteConfirm
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={executeDelete}
                deleteType={deleteType}
            />
        </Card>
    );
};

export default ReportCard;
